import type { LimitAndSortDescription } from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../../../../types/candidate";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { t } from "i18next";
import { FIND_RECORD_LIMIT_SORT_ID } from "../../../../../../../../utils/constants";
import { StyledGroupWrapper } from "../../../style";
import { LimitInput } from "./limitInput";
import { SortByInput } from "./sortByInput";
import { SortDirectionInput } from "./sortDirectionInput";
import {
  StyledLimitItem,
  StyledLimitSortWrapper,
  StyledSortByItem,
  StyledSortDirectionItem,
} from "./style";
import { getSortDirectionOptionsByFieldType } from "./utils";

export const LimitSortEditor = observer<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  action?: ActionCandidate;

  limitAndSortData: LimitAndSortDescription;
}>(({ onChange, input, action, limitAndSortData }) => {
  const [limit, setLimit] = useState(100);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("ASC");
  const [sortDirectionOptions, setSortDirectionOptions] = useState<
    Array<Record<string, unknown>>
  >([]);
  const { getFieldMissingErrorMessage } = useFieldMissingValidation();

  const getSortDirectionOptions = (): Array<Record<string, unknown>> => {
    const fieldSelected = limitAndSortData.fields.find(
      (field) => field.id === sortBy,
    );
    if (fieldSelected === undefined) {
      return [];
    }
    return getSortDirectionOptionsByFieldType(fieldSelected.type);
  };

  useEffect(() => {
    if (input.value === undefined || input.value.values.length === 0) {
      const defaultField = limitAndSortData.fields.find(
        (field) => field.default,
      );
      const sortBy = defaultField !== undefined ? defaultField.id : "";
      input.value = {
        values: [
          {
            value: {
              oneofKind: FIND_RECORD_LIMIT_SORT_ID,
              limitSort: {
                limit: 100,
                sortBy,
                sortDirection: "ASC",
              },
            },
          },
        ],
      };
      setSortBy(sortBy);
      onChange(input);
    } else {
      if (input.value.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID) {
        const { limit, sortBy, sortDirection } =
          input.value.values[0].value.limitSort;
        setLimit(limit);
        setSortBy(sortBy);
        setSortDirection(sortDirection);
      }
    }
  }, []);

  useEffect(() => {
    if (sortBy !== "") {
      setSortDirectionOptions(getSortDirectionOptions());
    }
  }, [sortBy]);

  const onChangeLimit = (value: string): void => {
    setLimit(Number(value));
    if (input.value?.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID) {
      input.value.values[0].value.limitSort.limit = Number(value);
      onChange(input);
    }
  };

  const onChangeSortBy = (value: string): void => {
    setSortBy(value);
    if (input.value?.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID) {
      input.value.values[0].value.limitSort.sortBy = value;
      onChange(input);
    }
  };

  const onChangeSortDirection = (value: string): void => {
    setSortDirection(value);
    if (input.value?.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID) {
      input.value.values[0].value.limitSort.sortDirection = value;
      onChange(input);
    }
  };

  if (
    input.value !== undefined &&
    input.value?.values.length > 0 &&
    input.value?.values[0].value.oneofKind !== FIND_RECORD_LIMIT_SORT_ID
  ) {
    return <></>;
  }

  if (
    input.value !== undefined &&
    input.value?.values.length > 0 &&
    input.value?.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID &&
    input.value?.values[0].value.limitSort.limit !== limit
  ) {
    setLimit(input.value?.values[0].value.limitSort.limit);
  }

  const isSortByFieldExists =
    sortBy === "" ||
    limitAndSortData.fields.some((field) => field.id === sortBy);
  const inputFieldMissingMessage = getFieldMissingErrorMessage(input, action);
  const sortFieldMissingMessage =
    isSortByFieldExists || sortBy === ""
      ? ""
      : String(t("automation-form.deleted-field-validation-message"));
  const fieldMissingErrorMessage =
    inputFieldMissingMessage || sortFieldMissingMessage;

  return (
    <StyledGroupWrapper>
      <StyledLimitSortWrapper>
        {limit !== 1 && (
          <StyledLimitItem>
            <LimitInput
              limit={limit}
              limitAndSortData={limitAndSortData}
              onChange={onChangeLimit}
            />
          </StyledLimitItem>
        )}
        <StyledSortByItem>
          <SortByInput
            value={sortBy}
            limitAndSortData={limitAndSortData}
            fieldMissingErrorMessage={fieldMissingErrorMessage}
            onChange={onChangeSortBy}
          />
        </StyledSortByItem>
        <StyledSortDirectionItem>
          <SortDirectionInput
            value={sortDirection}
            options={sortDirectionOptions}
            onChange={onChangeSortDirection}
          />
        </StyledSortDirectionItem>
      </StyledLimitSortWrapper>
    </StyledGroupWrapper>
  );
});
